/**
 * Writes the server has not taken yet, kept where an app kill cannot reach them.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §5 phase 3
 * and decision 9.3. Spec: the companion `-implementation-spec.md` §4.
 *
 * WHY THIS IS NOT LIKE ANY OTHER STORE ON THE DEVICE. Everything else the app
 * keeps locally — the localStorage read-caches, the IndexedDB notes corpus — is
 * a copy of something the server already has, so losing it costs a refetch. For
 * the moments an entry sits here, it is the ONLY copy of what the member typed.
 * That single difference decides the three choices below.
 *
 *   WHERE. `@capacitor/preferences` — `UserDefaults` on iOS, `SharedPreferences`
 *   on Android — not `localStorage` and not IndexedDB. All three survive an app
 *   kill, a force-quit and a reboot; the difference is that both WebKit stores
 *   can be cleared by the webview's own storage-pressure policy, which is the
 *   thing Capacitor's storage guidance points here to avoid. The corpus tolerates
 *   that and re-drains. This cannot.
 *
 *   WHAT. Sealed envelopes for an enrolled member, the plaintext body for one who
 *   is not (sealing is a pass-through for them, spec 6.7, decision 28). Either
 *   way an entry lives only from a `pause` or a kill to the next resume or
 *   launch of the SAME signed-in session: sign-out removes the member's whole
 *   queue (plan 9.3, rewritten 2026-09-15), after a bounded flush and, if that
 *   was not enough, one question. Nothing of a member's stays on a phone they
 *   have signed out of.
 *
 *   HOW LONG. 100 entries or 30 days, oldest evicted first. The cap bounds a
 *   signed-in member's own stale entries on a device that is never brought back
 *   online; it is not sized to anything survive a sign-out, because nothing does.
 *
 * ⚠️ THE GROUP IS CONFIGURED BEFORE EVERY CALL, and that is not defensive
 * padding. `Preferences.configure` swaps the plugin's ONE underlying store for
 * every caller in the process, and `courses-crypto/platform-key-adapter-android`
 * configures its own group before each of its calls for the same reason. Without
 * this, an outbox write landing after a key-sync read would go into the key-sync
 * file — which the Android backup rules name, and which is not where a member's
 * words belong.
 *
 * ⚠️ EVERY MUTATION RUNS ONE AT A TIME (2026-09-16). A member's queue is one
 * JSON array, and each change to it is a read, a filter and a write with awaits
 * between them. The engine has no cross-key cap, so a page turn with several
 * blanks — or a resume waking several backed-off rows — sends them together, and
 * each send writes its entry here before the request and removes it after. Two
 * of those interleaving lost one of the entries from disk: if the app was then
 * killed while that request was in the air, the write was never replayed, which
 * is the one loss this store exists to prevent, and nothing could measure it. In
 * the other order a landed entry came back from the dead and replayed on the
 * next launch, refused as stale — one spurious `conflictsYielded`, in the counter
 * the rollout watches. So every mutation joins one chain, and the read inside it
 * sees the write before it. The one read that only looks, `read`, is not
 * queued: a stale read there is what it always was, a snapshot.
 */
import { Preferences } from '@capacitor/preferences';

/** Its own store, so nothing else in the app can be configured over it. */
export const OUTBOX_GROUP = 'CoursesAutosave';
/** One key per member: their queue is theirs, on a shared phone as anywhere. */
export const outboxKey = memberId => `${OUTBOX_KEY_PREFIX}${memberId}`;
/** Every key of this store's starts with this; `sweepOthers` walks them by it. */
export const OUTBOX_KEY_PREFIX = 'courses-outbox:';

export const MAX_ENTRIES = 100;
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * @param {object} [deps]
 * @param {typeof Preferences} [deps.preferences]
 * @param {() => number} [deps.now]
 */
export function createOutbox({ preferences = Preferences, now = () => Date.now() } = {}) {
  const configure = () =>
    Promise.resolve(preferences.configure?.({ group: OUTBOX_GROUP })).catch(() => {});

  async function readJson(key, fallback) {
    try {
      await configure();
      const { value } = await preferences.get({ key });
      if (!value) return fallback;
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
      // A store that will not read is not a reason to fail a member's write.
      return fallback;
    }
  }

  async function writeJson(key, value) {
    try {
      await configure();
      await preferences.set({ key, value: JSON.stringify(value) });
      return true;
    } catch (error) {
      return false;
    }
  }

  async function removeKey(key) {
    try {
      await configure();
      await preferences.remove({ key });
    } catch (error) {
      /* nothing to do */
    }
  }

  // The mutation chain. Each mutation waits for the one before it, whatever
  // that one did; a mutation that throws does not break the chain for the next.
  let chain = Promise.resolve();
  function serialised(mutate) {
    const run = chain.then(mutate, mutate);
    chain = run.catch(() => {});
    return run;
  }

  /** Everything queued for one member, oldest first. */
  const read = memberId => readJson(outboxKey(memberId), []);

  /**
   * Add or replace the entry for a key. One per row, so a member typing for
   * five minutes leaves one entry, not three hundred.
   */
  async function putNow(memberId, entry) {
    if (memberId === null || memberId === undefined || !entry?.key) return false;
    const current = await read(memberId);
    const without = current.filter(e => e.key !== entry.key);
    const next = [
      ...without,
      { ...entry, member: memberId, enqueuedAt: entry.enqueuedAt ?? now() },
    ];
    // Oldest first, so the cap evicts the entry that has waited longest — the
    // one least likely still to matter.
    const capped = next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
    if (capped.length < next.length) {
      console.warn(
        `[Autosave] outbox is full (${MAX_ENTRIES}); dropped ${next.length - capped.length} of the oldest unsent edit(s).`,
      );
    }
    return writeJson(outboxKey(memberId), capped);
  }

  async function removeNow(memberId, key) {
    const current = await read(memberId);
    const left = current.filter(e => e.key !== key);
    if (left.length === current.length) return;
    if (!left.length) {
      await removeKey(outboxKey(memberId));
      return;
    }
    await writeJson(outboxKey(memberId), left);
  }

  /**
   * Drop what has waited too long, for one member. Returns how many went, so the
   * count can be reported rather than the loss being silent.
   */
  async function sweepMemberNow(memberId) {
    const current = await read(memberId);
    if (!current.length) return 0;
    const cutoff = now() - MAX_AGE_MS;
    const kept = current.filter(e => (e.enqueuedAt ?? 0) >= cutoff);
    if (kept.length === current.length) return 0;
    if (!kept.length) {
      await removeKey(outboxKey(memberId));
    } else {
      await writeJson(outboxKey(memberId), kept);
    }
    return current.length - kept.length;
  }

  /**
   * Remove every queue that is not the signed-in member's. Sign-out removes a
   * member's own queue, so in the ordinary course there is nothing here to find;
   * this catches a sign-out that died between starting and reaching that
   * removal, and the registry key an earlier build kept under the same prefix.
   * Returns how many keys went. A store without `keys()` answers zero.
   */
  async function sweepOthersNow(memberId) {
    let keys = [];
    try {
      await configure();
      keys = (await preferences.keys?.())?.keys ?? [];
    } catch (error) {
      return 0;
    }
    const mine = outboxKey(memberId);
    const stale = keys.filter(key => key.startsWith(OUTBOX_KEY_PREFIX) && key !== mine);
    for (const key of stale) await removeKey(key);
    return stale.length;
  }

  /** Everything of one member's, gone. Sign-out and encryption reset use this. */
  async function removeMemberNow(memberId) {
    await removeKey(outboxKey(memberId));
  }

  return {
    read,
    put: (memberId, entry) => serialised(() => putNow(memberId, entry)),
    remove: (memberId, key) => serialised(() => removeNow(memberId, key)),
    sweepMember: memberId => serialised(() => sweepMemberNow(memberId)),
    sweepOthers: memberId => serialised(() => sweepOthersNow(memberId)),
    removeMember: memberId => serialised(() => removeMemberNow(memberId)),
  };
}

/** The app instance. */
export const outbox = createOutbox();
